import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen animated-bg pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <a 
            href="/" 
            className="inline-flex items-center text-primary-400 hover:text-primary-300 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Zurück zur Startseite
          </a>
        </motion.div>

        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Datenschutzerklärung
          </h1>
          <p className="text-xl text-gray-300">
            Transparenz und Schutz Ihrer persönlichen Daten
          </p>
        </motion.div>

        {/* Content */}
        <motion.div 
          className="card space-y-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Datenschutz auf einen Blick</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Allgemeine Hinweise</h3>
                <p className="text-gray-300 leading-relaxed">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Datenerfassung auf dieser Website</h3>
                <p className="text-gray-300 leading-relaxed">
                  <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Allgemeine Hinweise und Pflichtinformationen</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Datenschutz</h3>
                <p className="text-gray-300 leading-relaxed">
                  Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Hinweis zur verantwortlichen Stelle</h3>
                <p className="text-gray-300 leading-relaxed">
                  Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
                </p>
                <div className="bg-dark-800/50 border border-primary-500/20 rounded-lg p-4 mt-3">
                  <p className="text-white">
                    <strong>Oliver Kriz</strong><br />
                    Amtstr. 22<br />
                    44575 Castrop-Rauxel<br />
                    E-Mail: olli@tech4.stream
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Datenerfassung auf dieser Website</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Cookies</h3>
                <p className="text-gray-300 leading-relaxed">
                  Diese Website verwendet keine Cookies zur Datenspeicherung. Alle Funktionen der Website funktionieren ohne Cookie-Speicherung.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Server-Log-Files</h3>
                <p className="text-gray-300 leading-relaxed mb-3">
                  Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log Files, die Ihr Browser automatisch an uns übermittelt. Dies sind:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Browsertyp und Browserversion</li>
                  <li>verwendetes Betriebssystem</li>
                  <li>Referrer URL</li>
                  <li>Hostname des zugreifenden Rechners</li>
                  <li>Uhrzeit der Serveranfrage</li>
                  <li>IP-Adresse</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Kontaktformular</h3>
                <p className="text-gray-300 leading-relaxed">
                  Wenn Sie uns per E-Mail oder WhatsApp kontaktieren, werden Ihre Angaben aus der Anfrage inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Amazon Affiliate Links</h3>
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-yellow-400">Wichtiger Hinweis:</strong> Diese Website enthält Amazon Affiliate Links. 
                    Wenn Sie über diese Links einkaufen, erhalten wir eine kleine Provision von Amazon. 
                    Für Sie entstehen dadurch keine zusätzlichen Kosten. 
                    Amazon kann dabei Daten über Ihren Einkauf sammeln, um die Provision korrekt zuzuordnen. 
                    Diese Datenverarbeitung erfolgt durch Amazon und unterliegt deren Datenschutzbestimmungen.
                  </p>
                  <p className="text-gray-300 leading-relaxed mt-3">
                    <strong>Weitere Informationen:</strong> 
                    <a href="https://www.amazon.de/gp/help/customer/display.html?nodeId=201909000" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="text-primary-400 hover:text-primary-300 underline">
                      Amazon Datenschutzerklärung
                    </a>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-400 mb-3">Thomann Partnerlinks</h3>
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-blue-400">Hinweis:</strong> Auf einigen Seiten verlinken wir auf Produkte bei Thomann (Partnerprogramm). 
                    Wenn Sie über diese Links einkaufen, erhalten wir ggf. eine Provision. Für Sie entstehen keine zusätzlichen Kosten. 
                    Die Zuordnung und Verarbeitung der hierfür erforderlichen Daten (z. B. Klick- und Bestellzuordnung) erfolgt durch Thomann 
                    und unterliegt deren Datenschutzbestimmungen.
                  </p>
                  <p className="text-gray-300 leading-relaxed mt-3">
                    <strong>Weitere Informationen:</strong> 
                    <a 
                      href="https://www.thomann.de/de/privacypolicy.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 underline"
                    >
                      Thomann Datenschutzerklärung
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Ihre Rechte</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Recht auf Auskunft</li>
              <li>Recht auf Berichtigung oder Löschung</li>
              <li>Recht auf Einschränkung der Verarbeitung</li>
              <li>Recht auf Widerspruch gegen die Verarbeitung</li>
              <li>Recht auf Datenübertragbarkeit</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Änderungen dieser Datenschutzerklärung</h2>
            <p className="text-gray-300 leading-relaxed">
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Kontakt</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten wenden Sie sich bitte an:
            </p>
            <div className="bg-dark-800/50 border border-primary-500/20 rounded-lg p-4">
              <p className="text-white">
                <strong>Oliver Kriz</strong><br />
                E-Mail: olli@tech4.stream
              </p>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
